/**
 * Gradient Contour Layer Component
 *
 * Renders water table elevation contours and flow direction arrows
 * overlaid on the map for visualizing site-specific groundwater gradients.
 *
 * Features:
 * - Contour lines with elevation labels
 * - Flow direction arrows
 * - Well water level markers
 * - Customizable visibility and styling
 */

import { useMemo } from 'react';
import { Polyline, Marker, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import { GradientContour, FlowArrow, GradientWellReading } from '../../api/polygon';

// ============================================================================
// TYPES
// ============================================================================

export interface GradientContourLayerProps {
  contours: GradientContour[];
  flowArrows?: FlowArrow[];
  wellReadings?: GradientWellReading[];
  visible: boolean;
  showLabels?: boolean;
  showFlowArrows?: boolean;
  showWellReadings?: boolean;
  labelInterval?: number; // Show labels every N contours
  contourColor?: string;
  majorContourColor?: string;
  flowArrowColor?: string;
  wellMarkerColor?: string;
  // Site bounds for coordinate conversion (if coordinates are relative 0-1 range)
  siteCenterLat?: number;
  siteCenterLon?: number;
  siteRadiusFt?: number; // Approximate site radius for scaling
  coordinatesType?: 'absolute' | 'relative';
  // Map compass direction - for correcting coordinates if map north is not "up"
  mapNorthDirection?: string; // "up", "right", "down", "left", "up-right", etc.
}

// ============================================================================
// CONSTANTS
// ============================================================================

const DEFAULT_CONTOUR_COLOR = '#0066cc';
const DEFAULT_MAJOR_CONTOUR_COLOR = '#003366';
const DEFAULT_FLOW_ARROW_COLOR = '#dc2626'; // Red for visibility
const DEFAULT_WELL_MARKER_COLOR = '#059669'; // Green

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Create an arrow icon for flow direction markers.
 * Arrow points in the direction of groundwater flow.
 */
function createArrowIcon(directionDeg: number, color: string = DEFAULT_FLOW_ARROW_COLOR, sizePx: number = 24): L.DivIcon {
  // Create an SVG arrow that rotates based on direction
  // Direction: 0=North, 90=East, 180=South, 270=West
  const half = Math.round(sizePx / 2);
  const svgArrow = `
    <svg width="${sizePx}" height="${sizePx}" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"
         style="transform: rotate(${directionDeg}deg);">
      <path d="M12 2 L18 14 L12 10 L6 14 Z"
            fill="${color}"
            stroke="#ffffff"
            stroke-width="1"/>
    </svg>
  `;

  return L.divIcon({
    html: svgArrow,
    className: 'gradient-flow-arrow',
    iconSize: [sizePx, sizePx],
    iconAnchor: [half, half],
  });
}

/**
 * Create a well marker icon.
 */
function createWellIcon(color: string = DEFAULT_WELL_MARKER_COLOR): L.DivIcon {
  const svgWell = `
    <svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="6"
              fill="${color}"
              stroke="#ffffff"
              stroke-width="2"/>
      <circle cx="8" cy="8" r="2"
              fill="#ffffff"/>
    </svg>
  `;

  return L.divIcon({
    html: svgWell,
    className: 'gradient-well-marker',
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}

/**
 * Determine if a contour is a major contour (for styling).
 * Major contours are typically every 5 or 10 ft, depending on interval.
 */
function isMajorContour(elevation: number | null | undefined, interval: number = 1): boolean {
  if (elevation === null || elevation === undefined) return false;
  // Major contour every 5 units if interval < 5, otherwise every interval
  const majorInterval = interval < 5 ? 5 : Math.ceil(interval);
  return Math.abs(elevation % majorInterval) < 0.01;
}

// ── Well-network coverage: solid contours inside the gauging-well hull
//    (interpolated from data), dashed outside (extrapolated). ───────────────
type LatLon = [number, number];

/** Convex hull (Andrew's monotone chain) of well points, as a [lat,lon] ring. */
function wellNetworkHull(points: { lat: number; lon: number }[]): LatLon[] {
  const pts = points
    .filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lon))
    .map(p => [p.lat, p.lon] as LatLon)
    .sort((a, b) => (a[1] - b[1]) || (a[0] - b[0])); // by lon, then lat
  if (pts.length < 3) return pts;
  const cross = (o: LatLon, a: LatLon, b: LatLon) =>
    (a[1] - o[1]) * (b[0] - o[0]) - (a[0] - o[0]) * (b[1] - o[1]);
  const lower: LatLon[] = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper: LatLon[] = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  return lower.slice(0, -1).concat(upper.slice(0, -1));
}

/** Ray-cast point-in-polygon. poly is a [lat,lon] ring (lat=y, lon=x). */
function pointInHull(lat: number, lon: number, poly: LatLon[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const yi = poly[i][0], xi = poly[i][1];
    const yj = poly[j][0], xj = poly[j][1];
    if (((yi > lat) !== (yj > lat)) && (lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
}

/** Split a contour polyline into consecutive runs that are inside vs outside
 *  the hull (classified per segment midpoint), so each run can render solid or
 *  dashed. With no usable hull, the whole line is treated as inside (solid). */
function splitContourByHull(positions: LatLon[], hull: LatLon[]): { pts: LatLon[]; inside: boolean }[] {
  if (hull.length < 3 || positions.length < 2) return [{ pts: positions, inside: true }];
  const runs: { pts: LatLon[]; inside: boolean }[] = [];
  let cur: LatLon[] = [positions[0]];
  let curInside: boolean | null = null;
  for (let i = 0; i < positions.length - 1; i++) {
    const a = positions[i], b = positions[i + 1];
    const segInside = pointInHull((a[0] + b[0]) / 2, (a[1] + b[1]) / 2, hull);
    if (curInside === null) curInside = segInside;
    if (segInside !== curInside) {
      runs.push({ pts: cur, inside: curInside });
      cur = [a];
      curInside = segInside;
    }
    cur.push(b);
  }
  if (cur.length >= 2) runs.push({ pts: cur, inside: curInside ?? true });
  return runs;
}

/**
 * Convert relative coordinates (0-1 range) to absolute lat/lon.
 * Assumes relative coords are centered around (0.5, 0.5) as center.
 *
 * @param relLat - Relative latitude (0-1)
 * @param relLon - Relative longitude (0-1)
 * @param centerLat - Site center latitude
 * @param centerLon - Site center longitude
 * @param radiusFt - Site radius in feet (for scaling)
 * @returns [absoluteLat, absoluteLon]
 */
function convertRelativeToAbsolute(
  relLat: number,
  relLon: number,
  centerLat: number,
  centerLon: number,
  radiusFt: number = 2000
): [number, number] {
  // Convert feet to degrees (approximate)
  // 1 degree latitude ≈ 364,000 ft
  // 1 degree longitude ≈ 364,000 ft × cos(lat)
  const ftPerDegLat = 364000;
  const ftPerDegLon = 364000 * Math.cos(centerLat * Math.PI / 180);

  // Relative 0-1 maps to -radiusFt to +radiusFt from center
  // So 0.5 is center, 0 is -radiusFt, 1 is +radiusFt
  const offsetFtLat = (relLat - 0.5) * 2 * radiusFt;
  const offsetFtLon = (relLon - 0.5) * 2 * radiusFt;

  // Convert offset from feet to degrees
  const offsetDegLat = offsetFtLat / ftPerDegLat;
  const offsetDegLon = offsetFtLon / ftPerDegLon;

  return [centerLat + offsetDegLat, centerLon + offsetDegLon];
}

/**
 * Check if coordinates appear to be in relative 0-1 range vs absolute lat/lon.
 *
 * Relative coordinates: Values in 0-1 range representing position on map image
 * Absolute coordinates: Real GPS lat/lon (e.g., lat 25-50 for US, lon -125 to -65)
 *
 * Detection logic:
 * 1. If any lat is outside -90 to 90 or any lon is outside -180 to 180, invalid
 * 2. If coordinates look like real-world lat/lon (typical ranges), they're absolute
 * 3. If all values are in 0-1 range (or close), they're relative
 */
function areCoordinatesRelative(coordinates: [number, number][]): boolean {
  if (!coordinates || coordinates.length === 0) return false;

  let allInRelativeRange = true;
  let anyLooksLikeRealLatLon = false;

  for (const [lat, lon] of coordinates) {
    // Check if values are clearly in relative 0-1 range
    if (lat < -0.1 || lat > 1.1 || lon < -0.1 || lon > 1.1) {
      allInRelativeRange = false;
    }

    // Check if values look like real-world lat/lon coordinates
    // US latitudes: ~25 to ~49 (lower 48) or ~18 to ~71 (including Alaska, Hawaii, PR)
    // US longitudes: ~-125 to ~-65 (lower 48) or ~-180 to ~-65 (including Alaska, Hawaii)
    // Texas specifically: lat 25.8-36.5, lon -106.6 to -93.5
    const looksLikeLatitude = Math.abs(lat) > 10 && Math.abs(lat) <= 90;
    const looksLikeLongitude = Math.abs(lon) > 10 && Math.abs(lon) <= 180;

    if (looksLikeLatitude || looksLikeLongitude) {
      anyLooksLikeRealLatLon = true;
    }
  }

  // If any coordinate looks like real lat/lon, treat as absolute
  if (anyLooksLikeRealLatLon) {
    return false;
  }

  // If all coordinates are in 0-1 range, treat as relative
  return allInRelativeRange;
}

/**
 * Parse map_north_direction string to rotation angle in degrees.
 * The angle represents how much we need to rotate coordinates to correct them.
 *
 * If Gemini extracted coordinates thinking image-up is geographic-north,
 * but the map's north arrow actually points in a different direction,
 * we need to rotate the coordinates to correct this.
 *
 * @param direction - The map_north_direction value from extraction
 * @returns Rotation angle in degrees (counter-clockwise)
 */
function parseMapNorthDirection(direction?: string): number {
  if (!direction) return 0;

  const dir = direction.toLowerCase().trim();

  // Standard direction strings
  switch (dir) {
    case 'up':
    case 'n':
    case 'north':
    case '0':
      return 0; // No rotation needed

    case 'up-right':
    case 'ne':
    case 'northeast':
    case '45':
      return -45; // Rotate 45° clockwise to correct

    case 'right':
    case 'e':
    case 'east':
    case '90':
      return -90; // Rotate 90° clockwise to correct

    case 'down-right':
    case 'se':
    case 'southeast':
    case '135':
      return -135;

    case 'down':
    case 's':
    case 'south':
    case '180':
      return -180;

    case 'down-left':
    case 'sw':
    case 'southwest':
    case '225':
      return -225;

    case 'left':
    case 'w':
    case 'west':
    case '270':
      return -270;

    case 'up-left':
    case 'nw':
    case 'northwest':
    case '315':
      return -315;

    default:
      // Try to parse as a number (degrees)
      const angle = parseFloat(dir);
      if (!isNaN(angle)) {
        return -angle; // Negative because we need to correct/undo the rotation
      }
      return 0;
  }
}

/**
 * Rotate coordinates around a center point to correct for map compass orientation.
 *
 * @param lat - Original latitude
 * @param lon - Original longitude
 * @param centerLat - Center point latitude
 * @param centerLon - Center point longitude
 * @param angleDeg - Rotation angle in degrees (positive = counter-clockwise)
 * @returns Rotated [lat, lon]
 */
function rotateCoordinate(
  lat: number,
  lon: number,
  centerLat: number,
  centerLon: number,
  angleDeg: number
): [number, number] {
  if (angleDeg === 0) return [lat, lon];

  const angleRad = (angleDeg * Math.PI) / 180;
  const cosA = Math.cos(angleRad);
  const sinA = Math.sin(angleRad);

  // Offset from center
  const dLat = lat - centerLat;
  const dLon = lon - centerLon;

  // Apply rotation (in lat/lon space - approximately correct for small areas)
  // Note: For more accuracy, would need to project to meters first
  const rotatedDLat = dLat * cosA - dLon * sinA;
  const rotatedDLon = dLat * sinA + dLon * cosA;

  return [centerLat + rotatedDLat, centerLon + rotatedDLon];
}

// ============================================================================
// COMPONENT
// ============================================================================

export function GradientContourLayer({
  contours,
  flowArrows,
  wellReadings,
  visible,
  showLabels = true,
  showFlowArrows = true,
  showWellReadings = true,
  labelInterval = 2,
  contourColor = DEFAULT_CONTOUR_COLOR,
  majorContourColor = DEFAULT_MAJOR_CONTOUR_COLOR,
  flowArrowColor = DEFAULT_FLOW_ARROW_COLOR,
  wellMarkerColor = DEFAULT_WELL_MARKER_COLOR,
  siteCenterLat,
  siteCenterLon,
  siteRadiusFt = 2000,
  coordinatesType,
  mapNorthDirection,
}: GradientContourLayerProps) {
  // Don't render anything if not visible
  if (!visible) return null;

  // Determine if we need to convert coordinates
  // Auto-detect if coordinatesType not specified
  const needsConversion = useMemo(() => {
    if (coordinatesType === 'absolute') return false;
    if (coordinatesType === 'relative') return true;

    // Auto-detect: check first contour's coordinates
    if (contours.length > 0 && contours[0].coordinates.length > 0) {
      return areCoordinatesRelative(contours[0].coordinates);
    }
    return false;
  }, [contours, coordinatesType]);

  // Calculate rotation angle from map north direction
  const rotationAngle = useMemo(() => {
    return parseMapNorthDirection(mapNorthDirection);
  }, [mapNorthDirection]);


  // Calculate contour interval from the data
  const contourInterval = useMemo(() => {
    if (contours.length < 2) return 1;
    // Filter out contours with null/undefined elevation
    const validElevations = contours
      .map(c => c.elevation_ft)
      .filter((e): e is number => e !== null && e !== undefined)
      .sort((a, b) => a - b);
    if (validElevations.length < 2) return 1;
    const intervals = [];
    for (let i = 1; i < validElevations.length; i++) {
      const diff = Math.abs(validElevations[i] - validElevations[i - 1]);
      if (diff > 0.01) intervals.push(diff);
    }
    return intervals.length > 0 ? Math.min(...intervals) : 1;
  }, [contours]);

  // Sort contours by elevation for consistent rendering
  const sortedContours = useMemo(() => {
    return [...contours].sort((a, b) => {
      // Handle null/undefined elevations by putting them at the end
      if (a.elevation_ft === null || a.elevation_ft === undefined) return 1;
      if (b.elevation_ft === null || b.elevation_ft === undefined) return -1;
      return a.elevation_ft - b.elevation_ft;
    });
  }, [contours]);

  // Convert contour coordinates if needed (relative to absolute) and apply rotation
  const convertedContours = useMemo(() => {
    // First, handle relative to absolute conversion
    let processedContours = sortedContours;

    if (needsConversion && siteCenterLat && siteCenterLon) {
      processedContours = sortedContours.map(contour => ({
        ...contour,
        coordinates: contour.coordinates.map(([lat, lon]) =>
          convertRelativeToAbsolute(lat, lon, siteCenterLat, siteCenterLon, siteRadiusFt)
        ),
      }));
    }

    // Then apply rotation if needed (for non-standard map compass orientations)
    if (rotationAngle !== 0 && siteCenterLat && siteCenterLon) {
      processedContours = processedContours.map(contour => ({
        ...contour,
        coordinates: contour.coordinates.map(([lat, lon]) =>
          rotateCoordinate(lat, lon, siteCenterLat, siteCenterLon, rotationAngle)
        ),
      }));
    }

    return processedContours;
  }, [sortedContours, needsConversion, siteCenterLat, siteCenterLon, siteRadiusFt, rotationAngle]);

  // Convert flow arrows if needed (relative to absolute + rotation)
  const convertedFlowArrows = useMemo(() => {
    if (!flowArrows) return flowArrows;

    let processedArrows = flowArrows;

    // Convert relative to absolute if needed
    if (needsConversion && siteCenterLat && siteCenterLon) {
      processedArrows = flowArrows.map(arrow => {
        const [newLat, newLon] = convertRelativeToAbsolute(
          arrow.lat, arrow.lon, siteCenterLat, siteCenterLon, siteRadiusFt
        );
        return { ...arrow, lat: newLat, lon: newLon };
      });
    }

    // Apply rotation if needed
    if (rotationAngle !== 0 && siteCenterLat && siteCenterLon) {
      processedArrows = processedArrows.map(arrow => {
        const [newLat, newLon] = rotateCoordinate(
          arrow.lat, arrow.lon, siteCenterLat, siteCenterLon, rotationAngle
        );
        // Also rotate the flow direction angle (use 0 as fallback if null)
        const rotatedDirection = ((arrow.direction_deg ?? 0) + rotationAngle + 360) % 360;
        return { ...arrow, lat: newLat, lon: newLon, direction_deg: rotatedDirection };
      });
    }

    return processedArrows;
  }, [flowArrows, needsConversion, siteCenterLat, siteCenterLon, siteRadiusFt, rotationAngle]);

  // Convert well readings if needed (relative to absolute + rotation), filtering out invalid
  const convertedWellReadings = useMemo(() => {
    if (!wellReadings) return [];

    // First filter out wells without valid lat/lon
    const validWells = wellReadings.filter(well =>
      well.lat !== undefined && well.lat !== null &&
      well.lon !== undefined && well.lon !== null &&
      !isNaN(well.lat) && !isNaN(well.lon)
    );

    let processedWells = validWells;

    // Convert relative to absolute if needed
    if (needsConversion && siteCenterLat && siteCenterLon) {
      processedWells = validWells.map(well => {
        const [newLat, newLon] = convertRelativeToAbsolute(
          well.lat, well.lon, siteCenterLat, siteCenterLon, siteRadiusFt
        );
        return { ...well, lat: newLat, lon: newLon };
      });
    }

    // Apply rotation if needed
    if (rotationAngle !== 0 && siteCenterLat && siteCenterLon) {
      processedWells = processedWells.map(well => {
        const [newLat, newLon] = rotateCoordinate(
          well.lat, well.lon, siteCenterLat, siteCenterLon, rotationAngle
        );
        return { ...well, lat: newLat, lon: newLon };
      });
    }

    return processedWells;
  }, [wellReadings, needsConversion, siteCenterLat, siteCenterLon, siteRadiusFt, rotationAngle]);

  // Convex hull of the gauging wells = data-control boundary. Contour segments
  // inside it are interpolated (solid); outside it they're extrapolated (dashed).
  const wellHull = useMemo(() => wellNetworkHull(convertedWellReadings), [convertedWellReadings]);

  // Memoize arrow icons (use converted arrows), filtering out invalid coordinates and direction
  // Scale arrow size by magnitude (steeper gradient = larger arrow)
  const arrowIcons = useMemo(() => {
    if (!convertedFlowArrows) return [];
    const valid = convertedFlowArrows.filter(arrow =>
      arrow.lat !== undefined && arrow.lat !== null &&
      arrow.lon !== undefined && arrow.lon !== null &&
      !isNaN(arrow.lat) && !isNaN(arrow.lon) &&
      arrow.direction_deg !== undefined && arrow.direction_deg !== null
    );
    // Compute magnitude range for scaling (min 18px, max 34px)
    const mags = valid.map(a => a.magnitude ?? 0).filter(m => m > 0);
    const minMag = mags.length > 0 ? Math.min(...mags) : 0;
    const maxMag = mags.length > 0 ? Math.max(...mags) : 1;
    const magRange = maxMag - minMag || 1;

    return valid.map(arrow => {
      const norm = mags.length > 1 ? ((arrow.magnitude ?? 0) - minMag) / magRange : 0.5;
      const sizePx = Math.round(18 + norm * 16); // 18–34px
      return {
        arrow,
        icon: createArrowIcon(arrow.direction_deg!, flowArrowColor, sizePx),
      };
    });
  }, [convertedFlowArrows, flowArrowColor]);

  // Memoize well icon
  const wellIcon = useMemo(() => createWellIcon(wellMarkerColor), [wellMarkerColor]);

  return (
    <>
      {/* Water table elevation contour lines */}
      {convertedContours.map((contour, idx) => {
        // Skip contours with invalid elevation
        if (contour.elevation_ft === null || contour.elevation_ft === undefined) {
          return null;
        }

        const isMajor = isMajorContour(contour.elevation_ft, contourInterval);
        const showLabel = showLabels && (idx % labelInterval === 0 || isMajor);
        const lineColor = isMajor ? majorContourColor : contourColor;
        const lineWeight = isMajor ? 4 : 2;  // Increased from 2:1 for better visibility

        // Convert coordinates to Leaflet format
        const positions = contour.coordinates.map(
          ([lat, lon]) => [lat, lon] as [number, number]
        );

        if (positions.length < 2) return null;

        // Solid where the contour is inside the gauging-well hull (interpolated
        // from data); dashed where it extends outside the wells (extrapolated).
        const runs = splitContourByHull(positions, wellHull);

        return runs.map((run, runIdx) => (
          <Polyline
            key={`contour-${contour.elevation_ft}-${idx}-${runIdx}`}
            positions={run.pts}
            pathOptions={{
              color: lineColor,
              weight: lineWeight,
              opacity: 0.9,  // Increased from 0.7 for better visibility
              dashArray: run.inside ? undefined : '6,8',  // outside well coverage = extrapolated
            }}
          >
            {/* Label once per contour, on the first run. */}
            {runIdx === 0 && showLabel && contour.elevation_ft !== null && contour.elevation_ft !== undefined && (
              <Tooltip
                permanent
                direction="center"
                className="contour-label"
                offset={[0, 0]}
              >
                <span className="text-xs font-medium bg-white/80 px-1 rounded">
                  {contour.elevation_ft.toFixed(1)} ft
                </span>
              </Tooltip>
            )}
          </Polyline>
        ));
      })}

      {/* Flow direction arrows */}
      {showFlowArrows && arrowIcons.map(({ arrow, icon }, idx) => (
        <Marker
          key={`flow-arrow-${idx}`}
          position={[arrow.lat, arrow.lon]}
          icon={icon}
        >
          <Tooltip direction="top" offset={[0, -12]}>
            <div className="text-xs">
              {arrow.direction_deg !== null && arrow.direction_deg !== undefined && (
                <div>Flow: {arrow.direction_deg.toFixed(0)}°</div>
              )}
              {arrow.magnitude !== null && arrow.magnitude !== undefined && (
                <div>Gradient: {arrow.magnitude.toFixed(4)}</div>
              )}
            </div>
          </Tooltip>
        </Marker>
      ))}

      {/* Well water level markers */}
      {showWellReadings && convertedWellReadings?.map((well, idx) => (
        <Marker
          key={`well-reading-${well.well_id}-${idx}`}
          position={[well.lat, well.lon]}
          icon={wellIcon}
        >
          <Tooltip direction="top" offset={[0, -8]}>
            <div className="text-xs">
              <div className="font-medium">{well.well_id}</div>
              {well.wt_elev_ft !== null && well.wt_elev_ft !== undefined && (
                <div>WT Elev: {well.wt_elev_ft.toFixed(1)} ft</div>
              )}
            </div>
          </Tooltip>
        </Marker>
      ))}
    </>
  );
}

// ============================================================================
// TOGGLE CONTROL COMPONENT
// ============================================================================

export interface GradientLayerToggleProps {
  showContours: boolean;
  onToggle: (show: boolean) => void;
  hasGradientData: boolean;
  averageGradient?: number;
  averageFlowDirection?: number;
  source?: 'extracted' | 'interpolated';
}

export function GradientLayerToggle({
  showContours,
  onToggle,
  hasGradientData,
  averageGradient,
  averageFlowDirection,
  source,
}: GradientLayerToggleProps) {
  if (!hasGradientData) {
    return (
      <div className="text-sm text-gray-500 italic">
        No gradient data available
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={showContours}
          onChange={(e) => onToggle(e.target.checked)}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <span className="text-sm font-medium">Show Gradient Contours</span>
        {source && (
          <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
            {source}
          </span>
        )}
      </label>

      {showContours && (
        <div className="ml-6 text-xs text-gray-600 space-y-1">
          {averageGradient !== undefined && averageGradient !== null && (
            <div>Avg Gradient: {averageGradient.toFixed(4)} ft/ft</div>
          )}
          {averageFlowDirection !== undefined && averageFlowDirection !== null && (
            <div>Avg Flow Dir: {averageFlowDirection.toFixed(0)}°</div>
          )}
        </div>
      )}
    </div>
  );
}

export default GradientContourLayer;
